// File: ArticleCanadianRockiesAccommodations.jsx

import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleCanadianRockiesAccommodations() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "canadian-rockies-accommodations-guide";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Types of Accommodations",
    "section-2": "Top Locations to Stay",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://parks.canada.ca/voyage-travel/hebergement-accommodation",
      label: "Parks Canada: Camping and Accommodation"
    },
    {
      url: "https://www.banfflakelouise.com/accommodation",
      label: "Banff & Lake Louise Tourism – Accommodation Guide"
    },
    {
      url: "https://www.jasper.travel/hotels",
      label: "Tourism Jasper – Hotels & Lodging"
    }
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    // Banff
    {
      id: "banff",
      lat: 51.17778,
      lng: -115.57361,
      popupText: "Banff, Alberta"
    },
    // Lake Louise
    {
      id: "lake-louise",
      lat: 51.42528,
      lng: -116.18056,
      popupText: "Lake Louise, Alberta"
    },
    // Jasper
    {
      id: "jasper",
      lat: 52.87306,
      lng: -118.08222,
      popupText: "Jasper, Alberta"
    },
    // Canmore
    {
      id: "canmore",
      lat: 51.08335,
      lng: -115.35206,
      popupText: "Canmore, Alberta"
    },
    // Field (Yoho)
    {
      id: "field",
      lat: 51.39583,
      lng: -116.48889,
      popupText: "Field, Yoho National Park, BC"
    },
    // Kananaskis Village
    {
      id: "kananaskis",
      lat: 50.91556,
      lng: -115.14167,
      popupText: "Kananaskis Village, Alberta"
    },
    // Waterton Park
    {
      id: "waterton",
      lat: 49.05170,
      lng: -113.91420,
      popupText: "Waterton Lakes National Park, Alberta"
    }
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Accommodations in the Canadian Rockies</title>
        <meta
          name="description"
          content="A thorough, practical guide on Canadian Rockies accommodations, from luxury resorts to budget camping, with tips and local regulations."
        />
        <meta
          name="keywords"
          content="Canadian Rockies, accommodations, Banff, Jasper, Lake Louise, camping, hotels, hostels"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/canadian-rockies-accommodations-guide.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Accommodations in the Canadian Rockies"
        />
        <meta
          property="og:description"
          content="Discover top places to stay, local regulations, tips, and resources to plan your Canadian Rockies adventure."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/canadian-rockies-accommodations-guide.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/canadian-rockies-accommodations-guide.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Accommodations in the Canadian Rockies"
        />
        <meta
          name="twitter:description"
          content="Discover top places to stay, local regulations, tips, and resources to plan your Canadian Rockies adventure."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/canadian-rockies-accommodations-guide.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Panoramic view of Canadian Rockies"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              The Canadian Rockies rank among the most spectacular mountain
              ranges in the world, drawing travelers with their pristine lakes,
              towering peaks, and abundant wildlife. Whether you’re seeking a
              luxurious getaway or a budget-friendly camping experience, finding
              the right place to stay is crucial to making the most of your
              Rocky Mountain adventure. In this guide, we’ll explore the
              different accommodation options, highlight top destinations—each
              with map coordinates—and discuss important local regulations to
              help you have a safe, memorable trip.
            </p>

            {/* SECTION: Types of Accommodations */}
            <h2 id="section-1">Types of Accommodations</h2>
            <p>
              The Canadian Rockies cater to a wide variety of travel styles and
              budgets. From opulent hotels with grand dining halls to cozy
              cabins by a tranquil river, here are the main types of
              accommodations to consider:
            </p>
            <ul>
              <li>
                <strong>Luxury Hotels & Resorts:</strong> World-famous places
                like Fairmont Banff Springs or Fairmont Château Lake Louise
                offer top-notch service, spas, and breathtaking vistas.
              </li>
              <li>
                <strong>Mountain Lodges & Cabins:</strong> Ideal for those who
                want to stay close to nature without sacrificing too many
                comforts. Many are found along scenic roads or require a short
                hike.
              </li>
              <li>
                <strong>Budget Hotels & Hostels:</strong> Perfect if you're
                traveling on a tighter budget. Expect simple rooms or shared
                dorms, but often with communal kitchens and friendly atmospheres.
              </li>
              <li>
                <strong>Campgrounds & RV Parks:</strong> There are numerous
                frontcountry and backcountry sites throughout Banff, Jasper, Yoho,
                Kootenay, and Waterton Lakes National Parks, offering everything
                from basic tent pads to full RV hookups.
              </li>
            </ul>

            {/* SECTION: Top Locations to Stay */}
            <h2 id="section-2">Top Locations to Stay</h2>
            <p>
              From iconic resort towns to quieter villages, here are some of the
              best places to set up your base camp or settle in for a relaxing
              vacation:
            </p>

            <h3>Banff, Alberta</h3>
            <p
              ref={(el) => setSpotRef("banff", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={51.17778}
                coordX={-115.57361}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Banff is the most famous town in the Rockies, known for its
              lively atmosphere, wide range of hotels, and proximity to stunning
              sites like Lake Minnewanka. Accommodations range from luxury
              resorts to budget hostels, but they fill quickly in peak season.
            </p>

            <h3>Lake Louise, Alberta</h3>
            <p
              ref={(el) => setSpotRef("lake-louise", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={51.42528}
                coordX={-116.18056}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Famous for its turquoise lake and surrounding peaks, Lake Louise
              has limited accommodations (luxury hotel, inns, and a popular
              campground). Booking early is crucial as demand is extremely high.
            </p>

            <h3>Jasper, Alberta</h3>
            <p
              ref={(el) => setSpotRef("jasper", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={52.87306}
                coordX={-118.08222}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A laid-back mountain town in Jasper National Park. With options
              ranging from upscale lodges to campgrounds, Jasper is an excellent
              spot for those seeking a quieter, more remote feel.
            </p>

            <h3>Canmore, Alberta</h3>
            <p
              ref={(el) => setSpotRef("canmore", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={51.08335}
                coordX={-115.35206}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Just outside Banff National Park, Canmore often has more
              availability and slightly lower prices. It’s a convenient base for
              exploring both Banff and Kananaskis Country.
            </p>

            <h3>Field (Yoho National Park, BC)</h3>
            <p
              ref={(el) => setSpotRef("field", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={51.39583}
                coordX={-116.48889}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A tiny village in Yoho National Park close to Emerald Lake and
              Takakkaw Falls. Accommodations are limited, but it offers a
              peaceful escape from the busier towns.
            </p>

            <h3>Kananaskis Village, Alberta</h3>
            <p
              ref={(el) => setSpotRef("kananaskis", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={50.91556}
                coordX={-115.14167}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Located in Kananaskis Country, this area features a handful of
              resorts, lodges, and campgrounds. The region is known for its
              wilderness setting and excellent outdoor activities.
            </p>

            <h3>Waterton Park, Alberta</h3>
            <p
              ref={(el) => setSpotRef("waterton", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.05170}
                coordX={-113.91420}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Tucked away in the southern Rockies, Waterton Lakes National Park
              is quieter and known for its breathtaking mountain-lake scenery
              and abundant wildlife. Lodging is limited to a handful of hotels,
              cabins, and campgrounds. 
            </p>

            {/* LOCAL REGULATIONS & BEST PRACTICES */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              The Canadian Rockies include protected national parks and
              sensitive ecosystems, so specific rules apply:
            </p>
            <ul>
              <li>
                <strong>Park Pass Required:</strong> If staying in a national
                park (Banff, Jasper, etc.), purchase a valid Parks Canada entry
                pass and display it at all times.
              </li>
              <li>
                <strong>Wildlife Safety:</strong> Store food and garbage
                securely. Never feed wildlife or leave food unattended. Bear
                spray is recommended on backcountry trails.
              </li>
              <li>
                <strong>Camping Permits:</strong> Camp only in designated
                campgrounds or sites; backcountry camping requires a permit and
                adherence to designated routes.
              </li>
              <li>
                <strong>Fire Regulations:</strong> Use designated fire pits and
                check for fire bans. Firewood is typically supplied under a
                separate permit or fee.
              </li>
              <li>
                <strong>Leave No Trace:</strong> Pack out all waste, stay on
                trails, and avoid disturbing wildlife or natural features.
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need to book accommodations in advance?</h3>
              <p>
                Yes, especially during peak season (June–August) when hotels and
                campgrounds fill up months in advance. Reserve early to secure
                your preferred spot.
              </p>

              <h3>2. Is camping a good budget option?</h3>
              <p>
                Absolutely. National park campgrounds are relatively
                inexpensive, although you’ll need to plan ahead for reservations
                during the busy summer season.
              </p>

              <h3>3. Can I bring my pet?</h3>
              <p>
                Many hotels and campgrounds allow pets, but confirm in advance
                and be aware that national parks require pets to be leashed at
                all times.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From the iconic peaks of Banff to the serene shores of Waterton,
              the Canadian Rockies offer something for every traveler. Choosing
              the right accommodation type—be it luxury resort, rustic cabin, or
              scenic campground—can make your trip truly unforgettable. By
              following local regulations and booking in advance, you’ll be
              well-prepared to enjoy the unparalleled beauty of the Rocky
              Mountains. Pack your bags, plan your stay, and get ready for a
              once-in-a-lifetime adventure!
            </p>
            <p>
              <em>
                Disclaimer: Always verify official park guidelines, fire bans,
                and weather conditions when planning your stay. For the most
                up-to-date information, consult Parks Canada or local tourism
                boards.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}